import { useEffect, useMemo, useState } from "react";

import { ArrowRight, Bot, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import { useAIReview, usePostNote, type AIReviewResult, type Note, type Workspace } from "../api";
import { BOARD_META, type SwotBoard } from "../constants";

const NO_LANE = "__none__";
const SUGGESTED_NEW = "__suggested_new__";

/**
 * The posting gut-check. Opening the dialog immediately asks the local model
 * whether the note belongs on its board and which lane fits. The author always
 * has the last word: accept the AI's move, keep their board, answer its
 * question — and if Ollama is down, post anyway.
 */
export function PostNoteDialog({
  note,
  workspace,
  onOpenChange,
}: {
  note: Note | null;
  workspace: Workspace;
  onOpenChange: (open: boolean) => void;
}) {
  const review = useAIReview();
  const post = usePostNote(workspace.session.id);
  const [result, setResult] = useState<AIReviewResult | null>(null);
  const [aiFailed, setAiFailed] = useState(false);
  const [board, setBoard] = useState<SwotBoard | null>(null);
  const [lane, setLane] = useState<string>(NO_LANE);
  const [clarification, setClarification] = useState("");

  useEffect(() => {
    if (!note) return;
    setResult(null);
    setAiFailed(false);
    setBoard(note.board as SwotBoard);
    setLane(NO_LANE);
    setClarification("");
    review.mutate(note.id, {
      onSuccess: (r) => {
        setResult(r);
        if (r.suggested_category) setLane(SUGGESTED_NEW);
      },
      onError: () => setAiFailed(true),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per note
  }, [note?.id]);

  const lanes = useMemo(
    () => workspace.categories.filter((c) => c.board === board),
    [workspace.categories, board],
  );

  // The AI's suggested lane might already exist on the target board.
  const suggestedExisting = useMemo(
    () =>
      result?.suggested_category
        ? lanes.find(
            (l) => l.name.toLowerCase() === result.suggested_category!.toLowerCase(),
          )
        : undefined,
    [lanes, result],
  );

  if (!note) return null;

  const doPost = () => {
    const chosenExisting =
      lane === SUGGESTED_NEW ? suggestedExisting : lanes.find((l) => l.id === lane);
    post.mutate(
      {
        id: note.id,
        board: board ?? (note.board as SwotBoard),
        category_id: chosenExisting?.id ?? null,
        new_category:
          lane === SUGGESTED_NEW && !chosenExisting ? result?.suggested_category : null,
        ai_clarification: clarification.trim() || null,
      },
      {
        onSuccess: () => {
          toast.success("Posted to the wall");
          onOpenChange(false);
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  const disagrees = result && !result.agrees;

  return (
    <Dialog open={note !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Post “{note.title}”</DialogTitle>
          <DialogDescription>
            A quick AI gut-check before it goes on the wall — you decide.
          </DialogDescription>
        </DialogHeader>

        {review.isPending ? (
          <div className="space-y-1 rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Asking the local model whether this belongs on{" "}
              {BOARD_META[note.board].label}…
            </div>
            <p className="pl-6 text-xs">
              No need to wait — post now and the AI will flag this note afterward
              if it disagrees.
            </p>
          </div>
        ) : aiFailed ? (
          <div className="rounded-md border border-warning/50 bg-warning-light p-3 text-sm text-foreground">
            The local model isn’t reachable right now — you can still post the note
            and categorize it by hand.
          </div>
        ) : result ? (
          <div className="space-y-3">
            <div className="flex items-start gap-2 rounded-md border bg-muted/40 p-3 text-sm">
              <Bot className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
              <div className="space-y-1.5">
                {disagrees ? (
                  <>
                    <p className="font-medium">
                      {result.question ??
                        `This might fit better on ${BOARD_META[result.suggested_board].label}.`}
                    </p>
                    {result.reasoning ? (
                      <p className="text-xs text-muted-foreground">{result.reasoning}</p>
                    ) : null}
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button
                        size="sm"
                        variant={board === result.suggested_board ? "default" : "outline"}
                        className="h-7 text-xs"
                        onClick={() => setBoard(result.suggested_board)}
                      >
                        <ArrowRight className="mr-1 h-3 w-3" />
                        Move to {BOARD_META[result.suggested_board].label}
                      </Button>
                      <Button
                        size="sm"
                        variant={board === note.board ? "default" : "outline"}
                        className="h-7 text-xs"
                        onClick={() => setBoard(note.board as SwotBoard)}
                      >
                        Keep on {BOARD_META[note.board].label}
                      </Button>
                    </div>
                  </>
                ) : (
                  <p>
                    Looks right for <Badge variant="secondary">{BOARD_META[note.board].label}</Badge>
                    {result.suggested_category ? (
                      <>
                        {" "}
                        — suggested theme:{" "}
                        <span className="font-medium">{result.suggested_category}</span>
                      </>
                    ) : null}
                  </p>
                )}
                {result.clarifying_questions.length ? (
                  <ul className="list-inside list-disc space-y-0.5 pt-1 text-xs text-muted-foreground">
                    {result.clarifying_questions.map((q) => (
                      <li key={q}>{q}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </div>

            {(disagrees || result.clarifying_questions.length > 0) && (
              <Textarea
                placeholder="Your answer / extra context (kept on the note for the group discussion)"
                value={clarification}
                onChange={(e) => setClarification(e.target.value)}
                rows={2}
              />
            )}
          </div>
        ) : null}

        <div className="space-y-1.5">
          <div className="text-xs font-medium text-muted-foreground">
            Lane on {board ? BOARD_META[board].label : ""}
          </div>
          <Select value={lane} onValueChange={setLane}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_LANE}>Uncategorized</SelectItem>
              {result?.suggested_category ? (
                <SelectItem value={SUGGESTED_NEW}>
                  <span className="inline-flex items-center gap-1">
                    <Sparkles className="h-3 w-3 text-brand" />
                    {result.suggested_category}
                    {suggestedExisting ? " (AI pick)" : " (new)"}
                  </span>
                </SelectItem>
              ) : null}
              {lanes
                .filter((l) => l.id !== suggestedExisting?.id)
                .map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={doPost} disabled={post.isPending}>
            {post.isPending ? "Posting…" : "Post to wall"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
